'use client'

import { useRef, useState, useEffect, useCallback } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'
import { getLenis } from '../hooks/useSmoothScroll'

gsap.registerPlugin(ScrollTrigger, useGSAP)


/**
 * SCENE 09: THE END
 * Definitive closing frame — the particle field collapses into a literal ORANGE
 * (HexField phase 7): abd·ORANGE made literal, the namesake as the closing image.
 */
export default function End() {
  const sectionRef = useRef<HTMLElement>(null)
  const [cairoTime, setCairoTime] = useState('')

  useEffect(() => {
    const update = () =>
      setCairoTime(
        new Date().toLocaleTimeString('en-US', {
          timeZone: 'Africa/Cairo',
          hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
        })
      )
    update()
    const id = setInterval(update, 1000)
    return () => clearInterval(id)
  }, [])

  const scrollToTop = useCallback(() => {
    const lenis = getLenis()
    if (lenis) lenis.scrollTo(0, { duration: 2 })
    else window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  useGSAP(
    () => {
      gsap.from('.end-label', {
        y: 20, opacity: 0, duration: 0.7, ease: 'power2.out',
        scrollTrigger: { trigger: '.end-label', start: 'top 90%' },
      })
      gsap.from('.end-name', {
        y: 80, opacity: 0, duration: 1.2, ease: 'power3.out',
        scrollTrigger: { trigger: '.end-name', start: 'top 85%' },
      })
      gsap.from('.end-tagline', {
        y: 30, opacity: 0, duration: 0.8, ease: 'power2.out',
        scrollTrigger: { trigger: '.end-tagline', start: 'top 88%' },
      })
      gsap.from('.end-top-btn', {
        y: 20, opacity: 0, duration: 0.6, ease: 'power2.out',
        scrollTrigger: { trigger: '.end-top-btn', start: 'top 95%' },
      })
      gsap.from('.end-bottom', {
        opacity: 0, duration: 0.5, ease: 'power2.out',
        scrollTrigger: { trigger: '.end-bottom', start: 'top 98%' },
      })

    },
    { scope: sectionRef },
  )

  return (
    <footer
      id="end"
      ref={sectionRef}
      data-scene="end"
      className="relative w-full flex flex-col"
      style={{ backgroundColor: 'var(--color-void)', minHeight: '85vh' }}
    >
      {/* Top gradient line */}
      <div
        className="w-full h-px"
        style={{ background: 'linear-gradient(90deg, transparent, var(--color-fire), transparent)' }}
      />

      {/* Static ORANGE — the calm/no-WebGL form of the particle orange. Shown only
          when the field is absent: on phones (particles are desktop-only) and under
          prefers-reduced-motion. CSS hides it whenever the live orange is running. */}
      <div
        className="end-static-orange absolute pointer-events-none"
        aria-hidden="true"
        style={{ zIndex: 1, top: '46%', left: '50%', transform: 'translate(-50%, -50%)', width: 'clamp(170px, 56vw, 300px)', opacity: 0.55 }}
      >
        <svg viewBox="0 0 100 100" width="100%" height="100%" fill="none">
          <circle cx="50" cy="50" r="46" stroke="var(--color-fire)" strokeWidth="2.6" opacity="0.9" />
          <circle cx="50" cy="50" r="41" stroke="var(--color-fire)" strokeWidth="0.7" opacity="0.55" />
          {Array.from({ length: 8 }).map((_, i) => {
            const a = (i / 8) * Math.PI * 2 - Math.PI / 2
            return <line key={i} x1="50" y1="50" x2={50 + Math.cos(a) * 40} y2={50 + Math.sin(a) * 40} stroke="var(--color-fire)" strokeWidth="0.6" opacity="0.35" />
          })}
          {Array.from({ length: 8 }).map((_, i) => {
            const a = ((i + 0.5) / 8) * Math.PI * 2 - Math.PI / 2
            return <circle key={`s${i}`} cx={50 + Math.cos(a) * 24} cy={50 + Math.sin(a) * 24} r="1.6" fill="var(--color-fire)" opacity="0.6" />
          })}
          <circle cx="50" cy="50" r="2.4" fill="var(--color-fire)" opacity="0.7" />
        </svg>
      </div>

      {/* Main content — z-3 lifts the text above the fixed particle canvas (z-2),
          so the orange reads as a backdrop stamp behind the wordmark. */}
      <div className="relative flex flex-col items-center justify-center flex-1 gap-6 px-6 py-20 text-center" style={{ zIndex: 3 }}>

        {/* Scene label */}
        <p
          className="end-label font-mono text-xs tracking-[0.35em] uppercase"
          style={{ color: 'var(--color-fire)' }}
        >
          09 ◇ The End
        </p>

        {/* Big signature */}
        <h2
          className="end-name font-display font-bold leading-none"
          style={{
            fontSize: 'clamp(2.75rem, 13vw, 11rem)',
            letterSpacing: '-0.04em',
            color: 'var(--color-light)',
            transition: 'transform 0.5s var(--ease-out-quart)',
            cursor: 'default',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.025)' }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)' }}
        >
          abd<span className="wordmark-glow" style={{ color: 'var(--color-fire)', display: 'inline-block' }}>orange</span><span aria-hidden="true" style={{ color: 'var(--color-fire)', display: 'inline-block', animation: 'blink 1.15s steps(1) infinite' }}>.</span>
        </h2>

        {/* Tagline */}
        <p
          className="end-tagline font-mono"
          style={{
            color: 'var(--color-fog)',
            fontSize: 'clamp(0.65rem, 2vw, 0.8rem)',
            letterSpacing: '0.18em',
          }}
        >
          Forged in the void &mdash; built to think.
        </p>

        {/* Colophon — build stack */}
        {/* <p
          className="end-tagline font-mono"
          style={{
            color: 'var(--color-stone)',
            fontSize: 'clamp(0.55rem, 1.6vw, 0.7rem)',
            letterSpacing: '0.22em',
          }}
        >
          Next.js · Three.js · GSAP · Tailwind
        </p> */}

        {/* Section index — real crawlable anchor links (Lenis-smoothed), styled as
            the machine's node map: NN / NAME with a hex tick between entries. */}
        {/* <nav
          aria-label="Sections"
          className="end-nav flex flex-wrap items-center justify-center gap-x-4 gap-y-2 my-2"
        >
          {[
            { num: '01', label: 'Void', href: '#void' },
            { num: '02', label: 'Signal', href: '#signal' },
            { num: '03', label: 'Arsenal', href: '#arsenal' },
            { num: '04', label: 'Path', href: '#path' },
            { num: '05', label: 'Forge', href: '#gallery' },
            { num: '06', label: 'Proof', href: '#proof' },
            { num: '07', label: 'Voices', href: '#voices' },
            { num: '08', label: 'Uplink', href: '#portal' },
          ].map(({ num, label, href }, i, arr) => (
            <span key={href} className="flex items-center gap-x-4">
              <a
                href={href}
                className="font-mono uppercase transition-colors duration-300"
                style={{ fontSize: 'clamp(0.58rem, 1.5vw, 0.68rem)', letterSpacing: '0.2em', color: 'var(--color-fog)', paddingBlock: '10px' }}
                data-cursor-label={label}
                onClick={(e) => {
                  e.preventDefault()
                  const el = document.querySelector(href)
                  const lenis = getLenis()
                  if (el && lenis) lenis.scrollTo(el as HTMLElement, { offset: 0, duration: 1.4 })
                  else if (el) el.scrollIntoView({ behavior: 'smooth' })
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-fire)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-fog)')}
              >
                <span style={{ color: 'var(--color-stone)' }}>{num}</span>&nbsp;{label}
              </a>
              {i < arr.length - 1 && (
                <svg width="6" height="6" viewBox="0 0 100 100" fill="none" aria-hidden="true">
                  <polygon points="50,5 89,27.5 89,72.5 50,95 11,72.5 11,27.5" fill="none" stroke="rgba(255,107,0,0.4)" strokeWidth="10" />
                </svg>
              )}
            </span>
          ))}
        </nav> */}

        {/* Social links */}
        {/* <div className="end-socials flex flex-wrap items-center justify-center gap-x-6 gap-y-3 my-2">
          {[
            { label: 'GitHub',    href: 'https://github.com/abdelrahman18036',        cursor: 'GitHub' },
            { label: 'LinkedIn',  href: 'https://linkedin.com/in/abdelrahman18036',    cursor: 'LinkedIn' },
            { label: 'Instagram', href: 'https://www.instagram.com/orange18036/',      cursor: 'Instagram' },
            { label: 'X',         href: 'https://x.com/orange18036',                   cursor: 'X' },
            { label: 'Email',     href: 'mailto:abdelrahman.18036@gmail.com',          cursor: 'Email' },
          ].map(({ label, href, cursor }) => (
            <a
              key={label}
              href={href}
              target={href.startsWith('mailto') ? undefined : '_blank'}
              rel="noopener noreferrer"
              className="end-social font-mono text-xs tracking-[0.2em] uppercase transition-colors duration-300"
              style={{ color: 'var(--color-fog)', display: 'inline-flex', alignItems: 'center', paddingBlock: '11px', paddingInline: '10px' }}
              data-cursor-label={cursor}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-fire)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-fog)')}
            >
              {label}
            </a>
          ))}
        </div> */}

        {/* Hex divider */}
        <div className="flex items-center gap-4 my-2">
          <div className="w-12 h-px" style={{ backgroundColor: 'var(--color-stone)' }} />
          <svg width="8" height="8" viewBox="0 0 100 100" fill="none">
            <polygon points="50,5 89,27.5 89,72.5 50,95 11,72.5 11,27.5" stroke="var(--color-fire)" strokeWidth="10" fill="none" />
          </svg>
          <div className="w-12 h-px" style={{ backgroundColor: 'var(--color-stone)' }} />
        </div>


        {/* Back to top */}
        <button
          onClick={scrollToTop}
          data-cursor-label="Top"
          aria-label="Scroll to top"
          className="end-top-btn group mt-6 flex items-center gap-3 font-mono text-xs tracking-[0.25em] uppercase transition-colors duration-300"
          style={{ color: 'var(--color-mist)', paddingBlock: '11px' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-fire)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-mist)')}
        >
          <span
            className="block h-px transition-all duration-500 group-hover:w-10"
            style={{ width: '1.5rem', backgroundColor: 'currentColor' }}
          />
          Back to Top
          <span
            className="block h-px transition-all duration-500 group-hover:w-10"
            style={{ width: '1.5rem', backgroundColor: 'currentColor' }}
          />
        </button>
      </div>

      {/* Bottom strip */}
      <div
        className="end-bottom w-full flex flex-wrap items-center justify-between gap-3"
        style={{ borderTop: '1px solid color-mix(in srgb, var(--color-stone) 25%, transparent)' }}
      >
        <span
          className="font-mono text-[10px] tracking-[0.25em] uppercase"
          style={{ color: 'var(--color-stone)' }}
        >
          Cairo — {cairoTime}
        </span>
        <div className="flex items-center gap-5">
          <a
            href="/privacy"
            data-cursor-label="Privacy"
            className="font-mono text-[10px] tracking-[0.25em] uppercase transition-colors duration-300"
            style={{ color: 'var(--color-stone)' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-fire)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-stone)')}
          >
            Privacy
          </a>
          <span
            className="font-mono text-[10px] tracking-[0.25em] uppercase"
            style={{ color: 'var(--color-stone)' }}
          >
            © 2021–{new Date().getFullYear()} abdorange
          </span>
        </div>
      </div>
    </footer>
  )
}
